import { usePDF } from '@react-pdf/renderer';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import type { TemplateBuilderConfig } from '@/types/template-builder';
import { StatementDocument } from '@/lib/react-pdf/StatementDocument';
import type { StatementDocumentData } from '@/lib/react-pdf/statement-data';
import { cn } from '@/lib/utils';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

export const ZOOM_STEPS = [0.1, 0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];

interface ReactPdfViewerProps {
  config: TemplateBuilderConfig;
  data: StatementDocumentData;
  zoomIndex: number;
  className?: string;
}

export function ReactPdfViewer({
  config,
  data,
  zoomIndex,
  className,
}: Readonly<ReactPdfViewerProps>) {
  const reactPdfDocument = useMemo(
    () => <StatementDocument config={config} data={data} />,
    [config, data],
  );

  const [instance, updateInstance] = usePDF({ document: reactPdfDocument });

  const [stableBlob, setStableBlob] = useState<Blob | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(600);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width) setContainerWidth(Math.floor(width) - 48);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    updateInstance(reactPdfDocument);
  }, [reactPdfDocument]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (instance.error) {
      setPdfError(instance.error);
    } else if (!instance.loading && instance.blob) {
      setStableBlob(instance.blob);
      setPdfError(null);
    }
  }, [instance.loading, instance.blob, instance.error]);

  const onLoadSuccess = useCallback(({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
  }, []);

  const onLoadError = useCallback((err: Error) => {
    setPdfError(err.message);
  }, []);

  const zoom = ZOOM_STEPS[zoomIndex];
  const pageWidth = Math.round(containerWidth * zoom);

  if (pdfError) {
    return (
      <div
        className={cn(
          'w-full h-full flex items-center justify-center',
          className,
        )}
      >
        <p className="text-sm text-destructive max-w-sm text-center">
          Preview error: {pdfError}
        </p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'w-full h-full overflow-auto flex flex-col items-center py-6 gap-4',
        className,
      )}
    >
      {!stableBlob ? (
        <div className="flex-1 flex items-center justify-center">
          <span className="text-sm text-muted-foreground">
            Generating preview…
          </span>
        </div>
      ) : (
        <Document
          file={stableBlob}
          onLoadSuccess={onLoadSuccess}
          onLoadError={onLoadError}
          loading={
            <div className="mt-8">
              <span className="text-sm text-muted-foreground">
                Loading pages…
              </span>
            </div>
          }
        >
          {Array.from({ length: numPages }, (_, i) => (
            <Page
              key={`page-${i + 1}`}
              pageNumber={i + 1}
              width={pageWidth}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              className="shadow-lg mb-4"
            />
          ))}
        </Document>
      )}
    </div>
  );
}
